import type { FC } from 'react'
import { TableBody, TableRow } from '@material-ui/core'
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"


const getItemStyle = (isDragging, draggableStyle) => ({
  // styles we need to apply on draggables
  ...draggableStyle,

  ...(isDragging && {
      background: "rgb(235,235,235)"
  })
})

export const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)

  return result
}

// IMPORTANT — these factories MUST return a *stable* component reference across
// renders. They are passed to MUI's `component` prop (<TableRow component={X}>),
// and React treats a brand-new component type as a different element: it would
// unmount/remount the whole row on every parent render. With controlled inputs
// (e.g. typing in a cell), each keystroke re-renders the parent, so an unstable
// component remounts the row and the input loses focus on every letter.
//
// To stay stable while still reflecting the current index / onDragEnd / disabled
// flag, we cache one component per draggableId and read the dynamic values from a
// mutable params object that is refreshed on every factory call (during the
// parent's render, before the child renders).

type DraggableEntry = { Comp: FC<any>; params: { index: number; enabled: boolean } }
const draggableCache: Record<string, DraggableEntry> = {}

export const DraggableComponent = (id: string, index: number, tableIsDraggabled = true) => {
  let entry = draggableCache[id]
  if (!entry) {
    const params = { index, enabled: tableIsDraggabled }
    const Comp: FC<any> = (props) => (
      <Draggable draggableId={id} index={params.index} enableUserSelectHack={false} handle=".just-input" isDragDisabled={!params.enabled}>
        {(provided, snapshot) => (
          <TableRow
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
            {...props}
          >
            {props.children}
          </TableRow>
        )}
      </Draggable>
    )
    entry = draggableCache[id] = { Comp, params }
  }
  // Refresh the dynamic values; the cached component reads them at render time.
  entry.params.index = index
  entry.params.enabled = tableIsDraggabled
  return entry.Comp
}

// The droppableId is a constant ('1'), so there is a single droppable on screen
// at a time — one cached component is enough. onDragEnd is kept in a mutable ref
// so the component identity never changes even though callers pass a fresh
// onDragEnd closure on every render.
const droppableParams: { onDragEnd: (result: any, provided: any) => void } = {
  onDragEnd: () => {},
}
let droppableComp: FC<any> | null = null

export const DroppableComponent = (onDragEnd: (result: any, provided: any) => void) => {
  droppableParams.onDragEnd = onDragEnd
  if (!droppableComp) {
    droppableComp = (props) => (
      <DragDropContext onDragEnd={(result, provided) => droppableParams.onDragEnd(result, provided)}>
        <Droppable droppableId={'1'} direction="vertical">
          {(provided) => {
            return (
              <TableBody ref={provided.innerRef} {...provided.droppableProps} {...props}>
                {props.children}
                {provided.placeholder}
              </TableBody>
            )
          }}
        </Droppable>
      </DragDropContext>
    )
  }
  return droppableComp
}
